import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Exercise } from '../../../../types/dataModel';
import { blurEditableOnEnter } from '../utils/trainerWorkoutEditUtils';
import { ExerciseService } from '../../../../lib/exerciseService';
import { filterAndSortExercisesByName } from '../../../../utils/exerciseSearch';
import { getYouTubeThumbnail } from '../../../../utils/youtubeUtils';
import { Check, ChevronRight, Plus, Search, X } from '../../bravosIcons';

export type TrainerWorkoutAddExerciseDrawerProps = {
  open: boolean;
  blockIndex: number;
  blockLabel: string;
  favoriteExerciseIds?: string[];
  libraryExercises?: Exercise[];
  onClose: () => void;
  onAdd: (exercises: Exercise[]) => void;
};

const FILTER_ALL = 'All';

export function TrainerWorkoutAddExerciseDrawer({
  open,
  blockIndex,
  blockLabel,
  favoriteExerciseIds = [],
  libraryExercises,
  onClose,
  onAdd,
}: TrainerWorkoutAddExerciseDrawerProps) {
  const [exercises, setExercises] = useState<Exercise[]>(libraryExercises ?? []);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState(FILTER_ALL);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) {
      setSearch('');
      setFilter(FILTER_ALL);
      setSelected(new Set());
      return;
    }
    if (libraryExercises?.length) {
      setExercises(libraryExercises);
      return;
    }
    let cancelled = false;
    setLoading(true);
    ExerciseService.getExercises()
      .then((list) => {
        if (!cancelled) setExercises(list ?? []);
      })
      .catch((err) => console.error('Load exercises for drawer:', err))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, libraryExercises]);

  const muscleFilters = useMemo(() => {
    const set = new Set<string>();
    exercises.forEach((e) => {
      const m = (e.muscleGroup || '').trim();
      if (m) set.add(m);
    });
    return [FILTER_ALL, ...Array.from(set).sort()];
  }, [exercises]);

  const filtered = useMemo(() => {
    const muscleFilter = filter === FILTER_ALL ? 'all' : filter;
    const list = filterAndSortExercisesByName(exercises, search, favoriteExerciseIds, muscleFilter);
    const favSet = new Set(favoriteExerciseIds);
    const recent = list.filter((e) => favSet.has(e.id));
    const rest = list.filter((e) => !favSet.has(e.id));
    const byMuscle = new Map<string, Exercise[]>();
    rest.forEach((e) => {
      const mg = (e.muscleGroup || 'Other').trim() || 'Other';
      if (!byMuscle.has(mg)) byMuscle.set(mg, []);
      byMuscle.get(mg)!.push(e);
    });
    return { recent, sections: Array.from(byMuscle.entries()).sort(([a], [b]) => a.localeCompare(b)) };
  }, [exercises, search, filter, favoriteExerciseIds]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const handleAdd = () => {
    const picked = exercises.filter((e) => selected.has(e.id));
    if (!picked.length) return;
    onAdd(picked);
    onClose();
  };

  if (!open) return null;

  const blockNum = String(blockIndex + 1).padStart(2, '0');

  const renderRow = (ex: Exercise) => {
    const thumb = getYouTubeThumbnail(ex.video) || ex.image || undefined;
    const isOn = selected.has(ex.id);
    const tags = [ex.muscleGroup, ex.equipment].filter(Boolean);
    return (
      <div
        key={ex.id}
        className={`exercise-list-item${isOn ? ' selected' : ''}`}
        onClick={() => toggle(ex.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle(ex.id);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div className="ex-list-thumb">
          {thumb ? <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
        </div>
        <div className="ex-list-info">
          <div className="ex-list-name">{ex.name}</div>
          <div className="ex-list-tags">
            {tags.map((t) => (
              <span key={t} className="ex-list-tag">
                {t}
              </span>
            ))}
          </div>
        </div>
        <button
          type="button"
          className={`ex-add-btn${isOn ? ' added' : ''}`}
          aria-pressed={isOn}
          onClick={(e) => {
            e.stopPropagation();
            toggle(ex.id);
          }}
        >
          {isOn ? (
            <Check width={12} height={12} aria-hidden />
          ) : (
            <Plus width={12} height={12} aria-hidden />
          )}
        </button>
      </div>
    );
  };

  return createPortal(
    <div className="twe-portal">
      <div className="scrim open" role="presentation" onClick={onClose} />
      <aside
        className="exercise-drawer open"
        role="dialog"
        aria-modal="true"
        aria-labelledby="twd-add-ex-title"
        onKeyDown={blurEditableOnEnter}
      >
        <div className="drawer-header">
          <div className="drawer-eyebrow">
            <div className="drawer-eyebrow-label">
              <span className="eyebrow-dot" aria-hidden />
              Add exercise — Block {blockNum}
            </div>
            <button type="button" className="drawer-close" onClick={onClose} aria-label="Close">
              <X width={16} height={16} aria-hidden />
            </button>
          </div>
          <div className="drawer-search-wrap">
            <Search className="search-icon" width={16} height={16} aria-hidden />
            <input
              className="drawer-search"
              type="search"
              placeholder="Search exercises…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="drawer-filters" role="tablist" aria-label="Filter exercises">
            {muscleFilters.slice(0, 8).map((f) => (
              <button
                key={f}
                type="button"
                role="tab"
                aria-selected={filter === f}
                className={`filter-pill${filter === f ? ' active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
        <div className="drawer-body">
          {loading && <p style={{ padding: '14px 24px', color: 'var(--ink-muted)' }}>Loading exercises…</p>}
          {!loading && filtered.recent.length > 0 && (
            <>
              <div className="drawer-section-label">Recently used</div>
              {filtered.recent.map(renderRow)}
            </>
          )}
          {!loading &&
            filtered.sections.map(([title, items]) => (
              <React.Fragment key={title}>
                <div className="drawer-section-label">{title}</div>
                {items.map(renderRow)}
              </React.Fragment>
            ))}
        </div>
        <div className="drawer-footer">
          <span className="drawer-footer-count">
            <strong>{selected.size}</strong> exercise{selected.size === 1 ? '' : 's'} selected
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn-primary" disabled={selected.size === 0} onClick={handleAdd}>
              Add to block
              <ChevronRight width={14} height={14} aria-hidden />
            </button>
          </div>
        </div>
      </aside>
    </div>,
    document.body,
  );
}
